import { useCallback, useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { getSessionToken } from "../../hooks/useSession.js";
import { Button } from "../ui/index.js";
import { SetupStepHeader } from "./SetupStepHeader.js";
import { formatCallDeliveryNumber } from "./setupSteps.js";
import { readDesktopAppConfirmed, setDesktopAppConfirmed } from "./setupStorage.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const WINDOWS_DOWNLOAD =
  "https://docs.netriovoip.com/mediawiki/software/netrio_6.5.3.2_110875.msi";
const MAC_DOWNLOAD =
  "https://docs.netriovoip.com/mediawiki/software/netrio_6.7.1.1_124243.dmg";
const RING_PORTAL_URL = "https://www.intelelead.com/portal/";

function DownloadLink({ label, href }: { label: string; href: string }) {
  return (
    <div>
      <p className="text-sm font-semibold text-content-primary">{label}</p>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="focus-ring mt-1 inline-block break-all rounded text-sm font-medium text-content-primary underline underline-offset-2"
      >
        {href}
      </a>
    </div>
  );
}

function NumberedSection({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className={index === 1 ? "" : "mt-7 border-t border-border-subtle pt-6"}>
      <div className="flex items-center gap-3">
        <span
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent-strong text-sm font-extrabold text-on-accent"
          aria-hidden
        >
          {index}
        </span>
        <h3 className="font-display text-xl font-extrabold leading-none tracking-[-0.03em] text-content-primary sm:text-2xl">
          {title}
        </h3>
      </div>
      <div className="mt-4 sm:pl-11">{children}</div>
    </section>
  );
}

export interface DialerCalibrationStepProps {
  agentId: string | null;
  softPhone: { callDeliveryNumber: string; username: string } | null;
  /** Staff impersonating an agent can confirm before credentials are assigned. */
  allowSkip?: boolean;
  /** Takes the Setup page back to the training videos. */
  onBack?: () => void;
  /** Fired once the agent confirms their desktop app is calibrated. */
  onStepComplete?: () => void;
}

/**
 * Step 4 of setup — what used to be "Step 10" at the bottom of the old dialer
 * guide. This is where the agent gets their Netrio desktop app downloads,
 * credentials, and call delivery number, and confirms they're calibrated.
 */
export function DialerCalibrationStep({
  agentId,
  softPhone,
  allowSkip = false,
  onBack,
  onStepComplete,
}: DialerCalibrationStepProps) {
  const token = getSessionToken();
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [revealingPassword, setRevealingPassword] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    setConfirmed(readDesktopAppConfirmed(agentId));
  }, [agentId]);

  const hasCredentials = Boolean(softPhone?.callDeliveryNumber && softPhone?.username);

  const revealPassword = useCallback(async () => {
    if (!token) return;
    setRevealingPassword(true);
    setRevealError(null);
    try {
      const response = await fetch(`${API_BASE}/api/services/DIALER_INTELELEAD/credentials/reveal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();
      if (!response.ok || !json.success || typeof json.data?.password !== "string") {
        throw new Error(json.error || "Unable to reveal your password");
      }
      setRevealedPassword(json.data.password);
    } catch (failure) {
      setRevealError(failure instanceof Error ? failure.message : "Unable to reveal your password");
    } finally {
      setRevealingPassword(false);
    }
  }, [token]);

  const confirmCalibration = useCallback(() => {
    setDesktopAppConfirmed(agentId, true);
    setConfirmed(true);
    onStepComplete?.();
  }, [agentId, onStepComplete]);

  return (
    <section className="space-y-8">
      <motion.header
        className="contents"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <SetupStepHeader stepId="desktopApp" complete={confirmed} />
      </motion.header>

      <p className="rounded-card border-2 border-status-nigo-border bg-status-nigo-bg px-5 py-4 text-sm font-semibold leading-6 text-status-nigo-text">
        Your dialer will not route calls to you until the desktop app is installed and your call delivery
        number points at it.
      </p>

      <article className="rounded-card border-2 border-border-default bg-surface-base px-5 py-5 shadow-card sm:px-7 sm:py-6">
        <NumberedSection index={1} title="Download the desktop app">
          <div className="space-y-4">
            <DownloadLink label="Windows" href={WINDOWS_DOWNLOAD} />
            <DownloadLink label="Mac" href={MAC_DOWNLOAD} />
          </div>
        </NumberedSection>

        <NumberedSection index={2} title="Log in with your credentials">
          <div className="space-y-3">
          <p className="text-sm text-content-secondary">
            These are case-sensitive. Use them to log in to the NeTrio desktop application.
          </p>
          <p className="text-sm text-content-secondary">
            Username:{" "}
            <span className="font-mono font-semibold text-content-primary">
              {softPhone?.username || "Assigned after provisioning finishes"}
            </span>
          </p>
          <div>
            {revealedPassword ? (
              <>
                <p className="text-sm text-content-secondary">
                  Password:{" "}
                  <span className="font-mono font-semibold text-content-primary">{revealedPassword}</span>
                </p>
                <Button className="mt-3" variant="secondary" onClick={() => setRevealedPassword(null)}>
                  Hide password
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                disabled={!hasCredentials || revealingPassword}
                onClick={() => void revealPassword()}
              >
                {revealingPassword
                  ? "Revealing…"
                  : hasCredentials
                    ? "Reveal password"
                    : "Password available after provisioning"}
              </Button>
            )}
            {revealError && (
              <p role="alert" className="mt-3 text-sm text-status-rejected-text">
                {revealError}
              </p>
            )}
          </div>
          <p className="text-sm text-content-secondary">
            Call delivery number:{" "}
            <span className="font-mono font-semibold text-content-primary">
              {formatCallDeliveryNumber(softPhone?.callDeliveryNumber)}
            </span>
          </p>
          </div>
        </NumberedSection>

        <NumberedSection index={3} title="Confirm in the Ring portal">
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-6 text-content-primary">
            <li>
              Confirm the Call Delivery Number in your{" "}
              <a
                href={RING_PORTAL_URL}
                target="_blank"
                rel="noreferrer"
                className="focus-ring rounded font-semibold text-content-primary underline underline-offset-2"
              >
                Ring Portal
              </a>
              .
            </li>
            <li>
              Make the <em>Call Delivery Number Name</em>{" "}
              <span className="font-semibold">First Name Last Name - Desktop App</span> — e.g.{" "}
              <span className="font-semibold">John Doe - Desktop App</span>.
            </li>
            <li>
              Set your <em>Call Delivery Number</em> on the Dashboard to{" "}
              {softPhone?.callDeliveryNumber ? (
                <span className="font-mono font-semibold text-content-primary">
                  {formatCallDeliveryNumber(softPhone.callDeliveryNumber)}
                </span>
              ) : (
                // Reads as a sentence either way — the formatter's placeholder
                // ("Assigned after provisioning finishes") would not here.
                <span className="font-semibold">your assigned call delivery number</span>
              )}
              .
            </li>
          </ol>
        </NumberedSection>

        <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-border-subtle pt-6">
          <Button variant="secondary" onClick={onBack}>
            <span aria-hidden="true">←</span>
            Back to training
          </Button>
          {confirmed ? (
            <p role="status" className="text-sm font-semibold text-status-active-text">
              Calibration confirmed — your setup is complete.
            </p>
          ) : (
            <Button size="lg" disabled={!hasCredentials && !allowSkip} onClick={confirmCalibration}>
              {hasCredentials || allowSkip ? "I'm calibrated — finish setup" : "Waiting on your credentials"}
              <span aria-hidden="true">→</span>
            </Button>
          )}
        </div>
      </article>
    </section>
  );
}
